// TODO - WPIP-67751: Implement flow when a user attempts to checkout
import classnames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import Alert from '@johnlewispartnership/wtr-ingredients/ingredients/Alert/index.js';
import Button, {
  type ButtonProps,
} from '@johnlewispartnership/wtr-ingredients/ingredients/Button/index.js';
import { Tooltip } from '@johnlewispartnership/wtr-ingredients/ingredients/Tooltip/index.js';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography/index.js';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { CheckoutConditions } from 'types/Checkout/checkout';
import { useTrolley } from 'contexts/Trolley';
import { urls } from 'constants/urls';
import { useSlotContext } from 'contexts/Slot';
import { navigateTo } from 'utils/location';
import { useCommonModal } from 'components/CommonModal';
import { getInstantCheckoutModal } from 'constants/modals/instantCheckoutModal';
import MinimumSpendNotMetMessage from './MinimumSpendNotMet/MinimumSpendNotMetMessage';
import styles from './GoToCheckout.module.scss';

type MessagePlacement = 'inline' | 'bottomPopover' | 'topPopover';

export interface GoToCheckoutProps {
  buttonCheckoutId?: string | undefined;
  fullWidth?: boolean;
  hideWhenDisabled?: boolean;
  messagePlacement?: MessagePlacement;
  theme?: ButtonProps['theme'];
  location?: string;
  useLightTooltip?: boolean;
}
export const GoToCheckout = ({
  buttonCheckoutId = undefined,
  fullWidth = false,
  hideWhenDisabled,
  messagePlacement = 'inline',
  theme = 'finalising',
  location = 'unknown',
  useLightTooltip = false,
}: GoToCheckoutProps) => {
  const { state: trolleyState } = useTrolley();
  const { trackEvent } = useAnalytics();
  const { openModal } = useCommonModal();

  // What happens here when window is not available (currently client side rendered so no issue)
  const isOnTrolleyPage = window.location.pathname === urls.trolleyPage;
  const isOnInterstitialsPage = window.location.pathname.includes(urls.interstitials);
  const hardConflicts = trolleyState.trolley?.trolleyTotals?.trolleyItemCounts?.hardConflicts;
  const hasHardConflictsOnTrolleyPage = isOnTrolleyPage && hardConflicts;
  const canInstantCheckout = trolleyState.instantCheckout === 'ALLOWED';
  const canShowInstantCheckoutModal = buttonCheckoutId === 'checkout-amend-order-alert';
  const {
    slotContext: { booked: slotBooked },
  } = useSlotContext();

  const destination = useMemo(() => {
    if (hardConflicts) {
      return urls.trolleyPage;
    }

    if (!slotBooked) {
      return urls.serviceSelection;
    }

    if (!isOnInterstitialsPage) {
      return `${urls.interstitials}${window.location.search || ''}`;
    }
    return urls.checkout;
  }, [hardConflicts, isOnInterstitialsPage, slotBooked]);
  const mimiumSpendThresholdMet = trolleyState?.trolley?.trolleyTotals?.minimumSpendThresholdMet;

  const conditions = [
    ...(hasHardConflictsOnTrolleyPage ? [CheckoutConditions.HAS_HARD_CONFLICTS] : []),
    ...(!mimiumSpendThresholdMet ? [CheckoutConditions.MINIMUM_SPEND_NOT_MET] : []),
  ];

  const disabled = conditions.length > 0;

  const goToCheckout = useCallback(() => {
    if (canShowInstantCheckoutModal) {
      // prevent sending the GA event in interstitial pages
      trackEvent({
        event: 'instant_checkout_trigger',
        instant_checkout: {
          available: canInstantCheckout,
          availability_reason: canInstantCheckout ? trolleyState.instantCheckout : 'NOT_AMENDING',
        },
      });
    }

    if (canInstantCheckout && canShowInstantCheckoutModal) {
      openModal(
        getInstantCheckoutModal({
          location,
          regularCheckoutDestination: destination,
        }),
      );
    } else {
      navigateTo(destination);
    }

    if (destination === urls.interstitials) {
      trackEvent({
        event: 'enter_checkout',
        location,
      });
    }
  }, [
    canInstantCheckout,
    canShowInstantCheckoutModal,
    destination,
    location,
    openModal,
    trackEvent,
    trolleyState.instantCheckout,
  ]);

  if (disabled && hideWhenDisabled) return null;

  const { HAS_HARD_CONFLICTS, MINIMUM_SPEND_NOT_MET } = CheckoutConditions;

  const inline = messagePlacement && messagePlacement.includes('inline');
  const buttonId = buttonCheckoutId ?? 'site-header-checkout';

  const inlineMessage = (
    <div id={`${buttonId}-disabled-msg`} className={styles.inlineMsg}>
      {conditions.find(condition => condition === MINIMUM_SPEND_NOT_MET) && (
        <MinimumSpendNotMetMessage isAlert />
      )}
      {conditions.find(condition => condition === HAS_HARD_CONFLICTS) && (
        <Alert
          type="warning"
          title="Unavailable items"
          message="Please remove all unavailable items from your trolley"
        />
      )}
    </div>
  );

  const tooltipMessage = (
    <div id="minimumOrder">
      {conditions.find(condition => condition === MINIMUM_SPEND_NOT_MET) && (
        <MinimumSpendNotMetMessage />
      )}
      {conditions.find(condition => condition === HAS_HARD_CONFLICTS) && (
        <Typography styleAs="paragraphHeading" noMargins>
          You have unavailable items in your trolley, please make the required changes in order to
          checkout
        </Typography>
      )}
    </div>
  );

  const checkoutButton = (
    <div className={styles.buttonOverlayTrigger}>
      <Button
        waiting={trolleyState?.loading}
        aria-describedby={`${buttonId}-disabled-msg`}
        aria-label="Checkout"
        data-testid="go-to-checkout-button"
        disabled={disabled}
        id={buttonId}
        label="Checkout"
        onClick={goToCheckout}
        theme={theme}
        type="submit"
        width="full"
      />
    </div>
  );

  const checkoutOrTooltip =
    messagePlacement.includes('Popover') && disabled ? (
      <Tooltip
        content={tooltipMessage}
        placement={messagePlacement.includes('top') ? 'top' : 'bottom'}
        className={classnames(styles.disabledTooltip, { [styles.light]: useLightTooltip })}
        disableFocusListener
      >
        {checkoutButton}
      </Tooltip>
    ) : (
      checkoutButton
    );

  return (
    <div
      className={classnames({ [styles.checkout]: !inline, [styles.fullSize]: fullWidth })}
      data-testid="go-to-checkout"
    >
      {checkoutOrTooltip}
      {inline && disabled && inlineMessage}
    </div>
  );
};
